/* ccs-card.js — Phase 1 product card behavior.
   - Click yellow ATC → reveal an inline qty picker (default 1). +/- adjust a
     LOCAL qty (no cart writes during selection). 2500 ms after the last click
     the card posts /cart/add.js with that qty, shows a toast, and reverts to
     the ATC button. Clicking − at qty 1 cancels back to ATC.
   - Same flow on every viewport.
   - Exposes window.CCSCard.wireAll(root) so dynamically-injected card grids
     can wire themselves after they swap in. Idempotent via data-ccs-wired.
   ES5-compatible per CCS rules — no arrows, const, template strings.
*/
(function () {
  'use strict';

  var COMMIT_DELAY_MS = 2500;

  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function wireForm(form) {
    if (form.dataset.ccsWired === '1') return;
    form.dataset.ccsWired = '1';

    /* stop ATC bubbles to outer card anchor */
    form.addEventListener('click', function (e) { e.stopPropagation(); });

    /* Defensive: prevent NATIVE form submit before any other logic. If the
       stepper-wiring path bails out below, this still guarantees the form
       won't navigate /cart/add → reload. The proper handler below replaces
       this with showStepper(). */
    form.addEventListener('submit', function (e) { e.preventDefault(); });

    var atcBtn = form.querySelector('.atc--card');
    var stepper = form.querySelector('.qty--card');
    var numEl = form.querySelector('.js-cart-num');
    var btnInc = form.querySelector('.js-cart-inc');
    var btnDec = form.querySelector('.js-cart-dec');
    var card = form.closest('.ccs-pc');
    var nameEl = card && card.querySelector('.ccs-pc__name');

    if (!atcBtn || !stepper || !numEl) {
      try { console.warn('[ccs-card] wireForm bailed — missing selector', { atc: !!atcBtn, stepper: !!stepper, num: !!numEl, form: form }); } catch (e) {}
      return;
    }

    var qty = 1;
    var commitTimer = null;
    var committing = false;

    function showAtc() {
      if (commitTimer) { clearTimeout(commitTimer); commitTimer = null; }
      committing = false;
      qty = 1;
      numEl.textContent = qty;
      stepper.hidden = true;
      atcBtn.hidden = false;
    }
    function showStepper() {
      qty = 1;
      numEl.textContent = qty;
      atcBtn.hidden = true;
      stepper.hidden = false;
      scheduleCommit();
    }
    function bumpQty(delta) {
      if (committing) return;
      var next = qty + delta;
      if (next <= 0) { showAtc(); return; }
      qty = Math.min(999, next);
      numEl.textContent = qty;
      scheduleCommit();
    }
    function scheduleCommit() {
      if (commitTimer) clearTimeout(commitTimer);
      commitTimer = setTimeout(commit, COMMIT_DELAY_MS);
    }
    function commit() {
      if (committing) return;
      committing = true;
      var commitQty = qty;
      stepper.classList.add('is-committing');
      /* Re-read variant + metafield props AT COMMIT TIME so variant-picker
         selections (ccsPickVariant updates form.dataset.variant on click)
         are honoured. Capturing at wire time was sending the original
         variant regardless of pick. */
      var idInput = form.querySelector('input[name="id"]');
      var currentVid = (idInput && idInput.value) || form.getAttribute('data-variant');
      var meta = {
        upc: form.getAttribute('data-units-per-case'),
        vol: form.getAttribute('data-volume-ml'),
        lbl: form.getAttribute('data-unit-label')
      };
      addToCart(currentVid, commitQty, meta, function (ok) {
        stepper.classList.remove('is-committing');
        if (ok) {
          var name = (nameEl && nameEl.textContent || 'Item').replace(/\s+/g, ' ').trim();
          /* Drawer is the canonical success feedback on every viewport (Phase 2). */
          if (typeof window.ccsCartDrawerOpen === 'function') {
            window.ccsCartDrawerOpen(commitQty + ' × ' + name + ' added to cart');
          }
          showAtc();
        } else {
          /* leave stepper open so user can retry */
          committing = false;
        }
      });
    }

    /* Click ATC → reveal stepper instead of submitting the form */
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      showStepper();
    });
    if (btnInc) btnInc.addEventListener('click', function (e) {
      e.preventDefault(); e.stopPropagation(); bumpQty(+1);
    });
    if (btnDec) btnDec.addEventListener('click', function (e) {
      e.preventDefault(); e.stopPropagation(); bumpQty(-1);
    });
  }

  /* Public entry — wire every card under `root`. Safe to call repeatedly. */
  function wireAll(root) {
    root = root || document;
    $$('.ccs-pc__atc-form', root).forEach(wireForm);
  }

  /* /cart/add.js — sends the same _units_per_case / _volume_ml / _unit_label
     line-item properties that ccsAddToCart does, so card adds merge into the
     same cart line as adds from PDP / hero / upsell surfaces. */
  function addToCart(variantId, qty, meta, done) {
    var body = { id: variantId, quantity: qty };
    var props = {};
    if (meta) {
      if (meta.upc && parseInt(meta.upc, 10) > 1) props._units_per_case = meta.upc;
      if (meta.vol && parseInt(meta.vol, 10) > 0) props._volume_ml      = meta.vol;
      if (meta.lbl)                               props._unit_label      = meta.lbl;
    }
    if (Object.keys(props).length > 0) body.properties = props;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function () { return { ok: r.ok }; });
    }).then(function (res) {
      done(res.ok);
    }).catch(function () { done(false); });
  }

  window.CCSCard = window.CCSCard || {};
  window.CCSCard.wireAll = wireAll;

  wireAll(document);

  /* Belt-and-suspenders defense against the form ever submitting natively.
     1) Strip the action attribute on every cart form (so even a bypassed
        submit goes nowhere meaningful).
     2) Capture-phase submit listener on document — runs before any other
        submit listener (incl. Shopify's #captcha-bootstrap), preventDefault. */
  function harden(root) {
    var forms = (root || document).querySelectorAll('.ccs-pc__atc-form');
    for (var i = 0; i < forms.length; i++) {
      forms[i].setAttribute('data-nocaptcha', 'true');
      forms[i].removeAttribute('action');
      forms[i].setAttribute('action', '#');
      /* Demote the submit button so clicking it can NEVER trigger any
         form-submission path (native, programmatic, or otherwise). The
         document-level click capture handler below handles the click and
         drives the stepper flow. */
      var btns = forms[i].querySelectorAll('button[type="submit"], button:not([type])');
      for (var j = 0; j < btns.length; j++) {
        btns[j].setAttribute('type', 'button');
      }
    }
  }
  harden(document);
  if (window.CCSCard) window.CCSCard.harden = harden;

  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (f && f.classList && f.classList.contains('ccs-pc__atc-form')) {
      e.preventDefault();
    }
  }, true);

  /* Document-level capture-phase click nuke: catch every ATC click before
     any other listener runs, kill default action + propagation. If the
     card's form was somehow not wired by wireForm above, this still
     prevents the parent anchor from navigating to the PDP and the form
     from submitting to /cart/add. Then we trigger the proper stepper flow
     via wireForm if it hasn't been wired yet. */
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('.atc--card, .js-atc-btn');
    if (!btn) return;
    var form = btn.closest('.ccs-pc__atc-form');
    if (!form) return;
    e.preventDefault();
    e.stopPropagation();
    /* Wire on demand so the click-to-stepper flow still works even if the
       initial wireAll missed this form for any reason. */
    wireForm(form);
    /* Programmatically reveal the stepper. The form's now-attached submit
       listener would do this on submit, but our preventDefault above stops
       the submit. So we kick the same effect manually. */
    var atcBtn = form.querySelector('.atc--card');
    var stepper = form.querySelector('.qty--card');
    var numEl = form.querySelector('.js-cart-num');
    if (atcBtn && stepper && numEl) {
      atcBtn.hidden = true;
      stepper.hidden = false;
      numEl.textContent = '1';
      /* Fire the form's submit event so any other downstream listeners
         (analytics, etc.) still run. ccs-card's own submit listener will
         preventDefault and call showStepper which is idempotent here. */
      try {
        var ev = document.createEvent('Event');
        ev.initEvent('submit', true, true);
        form.dispatchEvent(ev);
      } catch (err) {}
    }
  }, true); /* useCapture = true — runs before any bubble-phase listeners */
})();
